/**
 * Rekening Page
 * 
 * Displays bank accounts page with bank list and account management.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, CreditCard, Plus } from "lucide-react";
import { BankList } from "./components/BankList";
import { BankAccountTable } from "./components/BankAccountTable";
import { BankAccountDialog } from "./components/BankAccountDialog";
import { DeleteBankAccountDialog } from "./components/DeleteBankAccountDialog";
import { DeleteBankDialog } from "./components/DeleteBankDialog";
import { BankDialog } from "./components/BankDialog";
import { useBanks } from "./hooks/useBanks";
import { useBankAccounts } from "./hooks/useBankAccounts";
import { bankAccountService } from "@/services/bankAccountService";
import { bankService } from "@/services/bankService";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { hasAssignmentPrivilege } from "@/utils/privileges";

export function RekeningPage() {
  const { user } = useAuth();
  const canMutateRekening = hasAssignmentPrivilege(user, "create_transaction_bank_account");
  const canCreateBankAccount = canMutateRekening;
  const canManageBankAccounts = canMutateRekening;

  const [activeTab, setActiveTab] = React.useState<"banks" | "accounts">("banks");
  
  // Bank states
  const { banks, loading: banksLoading, error: banksError, fetchBanks } = useBanks();
  
  // Bank dialog states
  const [bankDialogOpen, setBankDialogOpen] = React.useState(false);
  const [selectedBank, setSelectedBank] = React.useState<any | null>(null);
  const [deleteBankDialogOpen, setDeleteBankDialogOpen] = React.useState(false);
  const [bankToDelete, setBankToDelete] = React.useState<any | null>(null);
  
  // Bank account states
  const { bankAccounts, loading: accountsLoading, error: accountsError, fetchBankAccounts } = useBankAccounts();
  
  // Bank account dialog states
  const [accountDialogOpen, setAccountDialogOpen] = React.useState(false);
  const [selectedAccount, setSelectedAccount] = React.useState<any | null>(null);
  
  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [accountToDelete, setAccountToDelete] = React.useState<any | null>(null);

  // Set page title
  React.useEffect(() => {
    document.title = "Rekening - ORARI";
  }, []);

  // Handle create account
  const handleCreateAccount = () => {
    setSelectedAccount(null);
    setAccountDialogOpen(true);
  };

  // Handle edit account
  const handleEditAccount = (account: any) => {
    setSelectedAccount(account);
    setAccountDialogOpen(true);
  };

  // Handle account dialog close
  const handleAccountDialogClose = (success: boolean = false) => {
    setAccountDialogOpen(false);
    setSelectedAccount(null);
    if (success) {
      fetchBankAccounts();
    }
  };

  // Handle delete account
  const handleDeleteAccount = (account: any) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!accountToDelete) return;

    try {
      await bankAccountService.deleteBankAccount(accountToDelete._id);
      toast.success("Rekening berhasil dihapus");
      fetchBankAccounts();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Gagal menghapus rekening";
      toast.error(errorMessage);
      throw error;
    }
  };

  // Handle create bank
  const handleCreateBank = () => {
    setSelectedBank(null);
    setBankDialogOpen(true);
  };

  // Handle bank dialog close
  const handleBankDialogClose = (success: boolean = false) => {
    setBankDialogOpen(false);
    setSelectedBank(null);
    if (success) {
      fetchBanks();
    }
  };

  // Handle edit bank
  const handleEditBank = (bank: any) => {
    setSelectedBank(bank);
    setBankDialogOpen(true);
  };

  // Handle delete bank
  const handleDeleteBank = (bank: any) => {
    setBankToDelete(bank);
    setDeleteBankDialogOpen(true);
  };

  // Handle confirm delete bank
  const handleConfirmDeleteBank = async () => {
    if (!bankToDelete) return;

    try {
      await bankService.deleteBank(bankToDelete._id);
      toast.success("Bank berhasil dihapus");
      fetchBanks();
      setDeleteBankDialogOpen(false);
      setBankToDelete(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Gagal menghapus bank";
      toast.error(errorMessage);
      throw error;
    }
  };

  return (
    <div className="flex-1 space-y-4 p-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Rekening</h2>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "banks" | "accounts")}>
        <TabsList>
          <TabsTrigger value="banks">
            <Building2 className="h-4 w-4 mr-2" />
            Daftar Bank
          </TabsTrigger>
          <TabsTrigger value="accounts">
            <CreditCard className="h-4 w-4 mr-2" />
            Rekening Bank
          </TabsTrigger>
        </TabsList>

        <TabsContent value="banks" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Daftar Bank</CardTitle>
                  <CardDescription>
                    Kelola daftar bank yang tersedia untuk rekening
                  </CardDescription>
                </div>
                <Button onClick={handleCreateBank}>
                  <Plus className="h-4 w-4 mr-2" />
                  Tambah Bank
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <BankList 
                banks={banks}
                loading={banksLoading}
                error={banksError}
                onRefresh={fetchBanks}
                onEdit={handleEditBank}
                onDelete={handleDeleteBank}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounts" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Rekening Bank</CardTitle>
                  <CardDescription>
                    Kelola rekening bank untuk transaksi
                  </CardDescription>
                </div>
                {canCreateBankAccount && (
                  <Button onClick={handleCreateAccount}>
                    <Plus className="h-4 w-4 mr-2" />
                    Tambah Rekening
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <BankAccountTable
                bankAccounts={bankAccounts}
                loading={accountsLoading}
                error={accountsError}
                onRefresh={fetchBankAccounts}
                onEdit={handleEditAccount}
                onDelete={handleDeleteAccount}
                allowMutate={canManageBankAccounts}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bank Account Dialog */}
      <BankAccountDialog
        open={accountDialogOpen}
        onClose={handleAccountDialogClose}
        account={selectedAccount}
        banks={banks}
      />

      {/* Delete Bank Account Dialog */}
      <DeleteBankAccountDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        account={accountToDelete}
        onConfirm={handleConfirmDelete}
      />

      {/* Bank Dialog */}
      <BankDialog
        open={bankDialogOpen}
        onOpenChange={handleBankDialogClose}
        bank={selectedBank}
        onSuccess={() => handleBankDialogClose(true)}
      />

      {/* Delete Bank Dialog */}
      <DeleteBankDialog
        open={deleteBankDialogOpen}
        onOpenChange={setDeleteBankDialogOpen}
        bank={bankToDelete}
        onConfirm={handleConfirmDeleteBank}
      />
    </div>
  );
}

